<template>
  <div>
    <el-dialog title="IntrustCA" :visible.sync="isOpen" width="500px" :before-close="handleClose"
      custom-class="intrust-ca-dialog">
      <template #title>
        <div class="flex items-center gap-2">
          <img width="40" src="@/assets/images/logo_intrust.png" alt="IntrustCA">
          <span class="text-lg font-semibold ml-2">
            IntrustCA
          </span>
        </div>
      </template>
      <!-- Content -->

      <div class="content">
        <!-- List Certificates -->
        <div class="mb-4" v-loading="loading" element-loading-text="Đang tải chứng thư..."
          element-loading-spinner="el-icon-loading">
          <label class="block text-sm font-semibold mb-2">Chứng thư số</label>
          <el-select v-model="selectedCertificateId" placeholder="Chọn chứng thư số" style="width: 100%" size="medium">
            <el-option v-for="cert in certificates" :key="cert.cert_serial" :label="cert.name"
              :value="cert.cert_serial">
              <div class="flex flex-col">
                <span class="font-medium">{{ cert.name }}</span>
              </div>
            </el-option>
          </el-select>
        </div>

        <!-- Certificate Info -->
        <div v-if="selectedCertificate" class="mb-4 p-3 bg-gray-50 rounded">
          <div class="text-sm">
            <div class="flex justify-between mb-2">
              <span class="text-gray-600">Chủ thể:</span>
              <span class="font-medium">{{ selectedCertificate.name || 'N/A' }}</span>
            </div>
            <div class="flex justify-between mb-2">
              <span class="text-gray-600">Nhà cung cấp:</span>
              <span class="font-medium">{{ selectedCertificate.cert_provider || 'IntrustCA' }}</span>
            </div>
            <div class="flex justify-between mb-2">
              <span class="text-gray-600">Thời hạn:</span>
              <span class="font-medium">
                {{ formatDate(selectedCertificate.cert_valid_from) }} - {{ formatDate(selectedCertificate.cert_valid_to)
                }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600">Trạng thái:</span>
              <span :class="selectedCertificate.status === 'VALID' ? 'text-green-600' : 'text-red-600'"
                class="font-medium">
                <span v-if="selectedCertificate.status === 'VALID'">🟢 Hợp lệ</span>
                <span v-else>🔴 {{ selectedCertificate.status }}</span>
              </span>
            </div>
          </div>
        </div>

        <!-- PIN Input -->
        <div v-if="selectedCertificate && selectedCertificate.auth_method === 2" class="mb-4">
          <label class="block text-sm font-semibold mb-2">
            Mã PIN <span class="text-red-500">*</span>
          </label>
          <el-input class="txt-grey-900 h-10" v-model="pin" type="password" placeholder="Nhập mã PIN" show-password
            clearable size="medium" @keyup.enter.native="handleSign">
            <i slot="prefix" class="el-input__icon el-icon-lock"></i>
          </el-input>
          <p class="text-xs text-gray-500 mt-1">
            Nhập mã PIN để xác thực và ký tài liệu
          </p>
        </div>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button class="fs-16" size="small" plain @click="handleClose">
          Đóng
        </el-button>
        <el-button class="fs-16" size="small" type="primary" @click="handleSign"
          :disabled="selectedCertificate && selectedCertificate.auth_method === 2 && (!selectedCertificateId || !pin)"
          :loading="signing">
          Ký
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import SignatureRequest from '@/api/request/SignatureRequest'

export default {
  name: 'ModalIntrustCA',
  data () {
    return {
      isOpen: false,
      loading: false,
      signing: false,
      certificates: [],
      selectedCertificateId: null,
      pin: ''
    }
  },
  computed: {
    selectedCertificate () {
      if (!this.selectedCertificateId) return null
      return this.certificates.find(cert => cert.cert_serial === this.selectedCertificateId)
    }
  },
  methods: {
    async handleOpen () {
      this.isOpen = true
      this.resetForm()
      await this.getCertificates()
    },
    handleClose () {
      this.isOpen = false
      this.resetForm()
    },
    resetForm () {
      this.selectedCertificateId = null
      this.pin = ''
      this.certificates = []
    },
    async getCertificates () {
      try {
        this.loading = true
        const request = new SignatureRequest()
        const response = await request.getIntrustCertificates()

        if (response.data && response.data.data && response.data.data.length > 0) {
          this.certificates = response.data.data
          // Auto select first certificate
          this.selectedCertificateId = this.certificates[0].cert_serial
        } else {
          this.certificates = []
          this.$message({
            type: 'warning',
            message: 'Không tìm thấy chứng thư số nào'
          })
        }
      } catch (error) {
        console.error('Get certificates error:', error)
        this.$message({
          type: 'error',
          message: error.response?.data?.message || 'Không thể lấy danh sách chứng thư số'
        })
      } finally {
        this.loading = false
      }
    },
    async handleSign () {
      try {
        if (!this.selectedCertificateId) {
          this.$message({
            type: 'warning',
            message: 'Vui lòng chọn chứng thư số'
          })
          return
        }

        if (this.selectedCertificate && this.selectedCertificate.auth_method === 2 && !this.pin) {
          this.$message({
            type: 'warning',
            message: 'Vui lòng nhập mã PIN'
          })
          return
        }

        this.signing = true

        const signData = {
          cert_serial: this.selectedCertificateId,
          pin: this.pin,
          // Use for mobile auth method
          auth_method: this.selectedCertificate.auth_method,
          key_id: this.selectedCertificate.key_id
        }

        this.$emit('onRequestSign', signData)
        this.handleClose()
      } catch (error) {
        console.error('Sign error:', error)
        this.$message({
          type: 'error',
          message: error.response?.data?.message || 'Ký thất bại'
        })
      } finally {
        this.signing = false
      }
    },
    formatDate (dateString) {
      if (!dateString) return 'N/A'
      return window.moment(dateString).format('DD/MM/YYYY')
    }
  }
}
</script>

<style scoped>
.intrust-ca-dialog {
  z-index: 9999 !important;
}

::v-deep .intrust-ca-dialog {
  z-index: 9999 !important;
  pointer-events: auto !important;

  * {
    pointer-events: auto !important;
  }

  input {
    pointer-events: auto !important;
    user-select: text !important;
    cursor: text !important;
  }
}

::v-deep .el-dialog__wrapper {
  z-index: 9999 !important;
  pointer-events: auto !important;
}

::v-deep .el-dialog__header {
  padding: 20px 20px 10px;
  border-bottom: 1px solid #f0f0f0;
}

::v-deep .el-dialog__body {
  padding: 20px;
  pointer-events: auto !important;
}

::v-deep .el-dialog__footer {
  padding: 10px 20px 20px;
  border-top: 1px solid #f0f0f0;
}

::v-deep .el-select-dropdown__item {
  height: auto;
  line-height: normal;
  padding: 10px 20px;
}

::v-deep .el-input {
  pointer-events: auto !important;
}

::v-deep .el-input__inner {
  pointer-events: auto !important;
  user-select: text !important;
  cursor: text !important;
}
</style>
